import React from 'react';

export interface CardProps {
  children: React.ReactNode;
  title?: string;
  footer?: React.ReactNode;
  elevation?: 'none' | 'sm' | 'md' | 'lg';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  onClick?: () => void;
  className?: string;
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  footer,
  elevation = 'md',
  padding = 'md',
  hoverable = false,
  onClick,
  className = '',
}) => {
  const elevationStyles = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  };

  const paddingStyles = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  const hoverStyle = hoverable ? 'hover:shadow-lg transition-shadow cursor-pointer' : '';
  const clickableStyle = onClick ? 'cursor-pointer' : '';
  const baseStyles = 'bg-white rounded-lg border border-gray-200';
  const cardClasses = `${baseStyles} ${elevationStyles[elevation]} ${hoverStyle} ${clickableStyle} ${className}`;
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    onClick();
  };

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {title ? (
        <div className={`border-b border-gray-200 ${paddingStyles[padding]}`}>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      ) : null}

      <div className={paddingStyles[padding]}>{children}</div>

      {footer ? (
        <div className={`border-t border-gray-200 ${paddingStyles[padding]}`}>{footer}</div>
      ) : null}
    </div>
  );
};

export default Card;
